import { FC, useEffect, useState } from 'react'
import { RenderIf } from 'components/RenderIf'
import { Button } from 'featureSliced/entities/Button'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { AmountHandler } from 'featureSliced/widgets/AmountHandler'
import { getAmountConfig } from 'featureSliced/shared/utils/weightSteps'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import {
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'

import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'

import { Divider, FooterContainer } from '../ComonProduct/styled'
import {
  IBucketProduct,
  IProductWithBucketProduct,
} from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'

type UpdateProductFooterProps = {
  product: IProductWithBucketProduct
  bucketProduct: IBucketProduct
  closeProduct: () => void
  currentAmount: number
  setCurrentAmount: (value: number) => void
}

export const UpdateProductFooter: FC<UpdateProductFooterProps> = ({
  product,
  bucketProduct,
  closeProduct,
  currentAmount,
  setCurrentAmount,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket

  // Store
  const tableStatus = useMySelector((state) => state.qrMenuTable.tableStatus)
  const bookingVariant = useMySelector((state) => state.app.menu.bookingVariant)
  const disabledByMobileWaiter = useMySelector(
    (state) => state.app.menu.disabledByMobileWaiter
  )

  // Functions
  const amountConfig = getAmountConfig(product.unitSellCode)
  const orderMin = product.ClickCollectOrderMin ?? amountConfig.minAmount
  const isLeadDaysExceeded = isItemLeadDaysExceeded({
    leadDays: getItemLeadDays(product.ClickCollectLeadDaysCount),
    pickUpTime: handleLocalStorage('pickUpTime'),
  })

  // Same rule as the add footer: a step that would land between 0 and the order
  // minimum stops at the minimum; only a step from the minimum empties the line.
  const handleDecreaseAmount = (value: number) => {
    if (value > 0 && value < orderMin) {
      setCurrentAmount(currentAmount > orderMin ? orderMin : 0)
      return
    }
    setCurrentAmount(value)
  }

  const updateProduct = () => {
    const productToAdd = {
      ...bucketProduct,
      count: currentAmount,
    }
    dispatch(addProductToBucket(productToAdd))
    closeProduct()
  }

  return (
    <RenderIf
      key={`${navigator.onLine}${bookingVariant}${disabledByMobileWaiter}${tableStatus}${isLeadDaysExceeded}`}
      condition={
        navigator.onLine &&
        !isLeadDaysExceeded &&
        (process.env.REACT_APP_IS_CLIC === '1' ||
          (bookingVariant === 2 &&
            !disabledByMobileWaiter &&
            tableStatus !== TableStatuses.BUSY))
      }
    >
      <Divider stacked={Boolean(amountConfig.coarseStep)} />
      <FooterContainer stacked={Boolean(amountConfig.coarseStep)}>
        <AmountHandler
          multiplier={amountConfig.multiplier}
          step={amountConfig.step}
          coarseStep={amountConfig.coarseStep}
          fineLabel={amountConfig.fineLabel}
          coarseLabel={amountConfig.coarseLabel}
          unit={amountConfig.unit}
          currentValue={currentAmount}
          onDecrease={handleDecreaseAmount}
          onIncrease={setCurrentAmount}
          maxValue={product.ClickCollectOrderMax ?? undefined}
        />
        <Button
          buttonType="common"
          disabled={tableStatus === TableStatuses.BUSY}
          width="100%"
          onClick={updateProduct}
        >
          {t('buttons.update')}
        </Button>
      </FooterContainer>
    </RenderIf>
  )
}
